import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { X, ChevronDown, ChevronRight, Trash2 } from 'lucide-react';
import { useEvent, useApiErrors } from '@soap/modules';
import { ThinkingAnimation } from '../../ThinkingAnimation';
import { Dialog, DialogContent } from '../../../ui/dialog';
import {
  parseEducationSystemDefinitionsEvent,
  getDefinitionForKey,
  getYearsForKeyStageId,
  isPlacementIdsValidForDefinition,
  type EducationSystemDefinition,
  type EducationSystemKeyStage
} from '../../../utils/educationSystemDefinitions';
import type { TopicMetadata } from '../types';
import type { CurriculumPlacementKey } from '../utils/curriculumUtils';
import {
  buildAddTopicsJobs,
  buildSubjectReviewCards,
  describeAddTopicsBatchProgress,
  needsPlacementOverride,
  collectEffectivePlacementsForAddedTopics,
  initialExpandedSubjectIdsForReview,
  prepareLeafTopicsForWizard,
  selectionUsesMultipleProviders,
  validateSingleEducationSystem,
  type AddTopicsJob,
  type PlacementOverride,
  type SubjectReviewCard,
  type ReviewYearSection
} from '../utils/groupTopicsForStudentCurriculumAdd';

const wizardSteps = {
  review: 'review',
  adding: 'adding',
  cancelling: 'cancelling',
  complete: 'complete'
} as const;

type WizardStep = (typeof wizardSteps)[keyof typeof wizardSteps];

export interface AddTopicsWizardDialogProps {
  showDialog: boolean;
  selectedTopics: any[];
  onClose: () => void;
  onRemoveTopicById: (topicId: string) => void;
  onClearSelection: () => void;
  buildBreadcrumb: (topic: any) => string;
  extractTopicMetadata: (topic: any) => TopicMetadata;
  studentEducationSystemKey: string;
  selectedStudentFirstName: string;
  defaultBuiltInProviderKey: string;
  studentId: string;
  onRunJob: (job: AddTopicsJob) => void;
  onWizardBatchingChange?: (active: boolean) => void;
  onLoadEducationSystemDefinitions?: () => void;
  onReloadStudentCurriculum?: () => void;
  /** Called when adds finish so the student tree can expand/load the new placements on the next E180. */
  onPrepareCurriculumTreeView?: (placements: CurriculumPlacementKey[]) => void;
  /** KS/year placements where topics were added — expand student tree on Create Units. */
  onViewStudentCurriculum: (placements: CurriculumPlacementKey[]) => void;
}

function educationSystemDisplayName(
  defs: EducationSystemDefinition[],
  key: string
): string {
  const k = (key || '').trim();
  if (!k) return 'Unknown framework';
  return getDefinitionForKey(defs, k)?.displayName || k;
}

export function AddTopicsWizardDialog({
  showDialog,
  selectedTopics,
  onClose,
  onRemoveTopicById,
  onClearSelection,
  buildBreadcrumb,
  extractTopicMetadata,
  studentEducationSystemKey,
  selectedStudentFirstName,
  defaultBuiltInProviderKey,
  studentId,
  onRunJob,
  onWizardBatchingChange,
  onLoadEducationSystemDefinitions,
  onReloadStudentCurriculum,
  onPrepareCurriculumTreeView,
  onViewStudentCurriculum
}: AddTopicsWizardDialogProps) {
  const [step, setStep] = useState<WizardStep>(wizardSteps.review);
  const [placementDefs, setPlacementDefs] = useState<EducationSystemDefinition[]>([]);
  const [placementByFolderKey, setPlacementByFolderKey] = useState<
    Record<string, PlacementOverride>
  >({});
  const [expandedSubjectIds, setExpandedSubjectIds] = useState<Set<string>>(new Set());
  const [topicsAddedCount, setTopicsAddedCount] = useState(0);
  const [addingProgress, setAddingProgress] = useState({
    added: 0,
    total: 0,
    batchIndex: 0,
    totalBatches: 0
  });

  const jobsRef = useRef<AddTopicsJob[]>([]);
  const jobIndexRef = useRef(0);
  const addedLeafTopicsRef = useRef<any[]>([]);
  const placementByFolderKeyRef = useRef<Record<string, PlacementOverride>>({});
  const stepRef = useRef<WizardStep>(wizardSteps.review);
  stepRef.current = step;
  placementByFolderKeyRef.current = placementByFolderKey;

  const leafTopics = useMemo(
    () => prepareLeafTopicsForWizard(selectedTopics),
    [selectedTopics]
  );

  const esValidation = useMemo(
    () => validateSingleEducationSystem(leafTopics, extractTopicMetadata),
    [leafTopics, extractTopicMetadata]
  );

  const offendingEducationSystemKeys = useMemo(() => {
    if (esValidation.isValid) return null;
    return new Set(esValidation.minorityKeys.map((k) => k.toLowerCase()));
  }, [esValidation.isValid, esValidation.minorityKeys]);

  const subjectCards = useMemo(
    () =>
      buildSubjectReviewCards(
        leafTopics,
        extractTopicMetadata,
        buildBreadcrumb,
        offendingEducationSystemKeys
      ),
    [leafTopics, extractTopicMetadata, buildBreadcrumb, offendingEducationSystemKeys]
  );

  const usesMultipleProviders = useMemo(
    () => selectionUsesMultipleProviders(leafTopics),
    [leafTopics]
  );

  const studentDef = useMemo(
    () => getDefinitionForKey(placementDefs, studentEducationSystemKey.trim()),
    [placementDefs, studentEducationSystemKey]
  );

  const keyStagesWithIds = useMemo(
    () => (studentDef?.keyStages ?? []).filter((k) => (k.keyStageId ?? '').trim() !== ''),
    [studentDef]
  );

  useEvent({
    eventName: 'E313v1_GotEducationSystemDefinitions',
    onEventReceived: (event: unknown) => {
      setPlacementDefs(parseEducationSystemDefinitionsEvent(event));
    }
  });

  useEffect(() => {
    if (!showDialog) return;
    onLoadEducationSystemDefinitions?.();
  }, [showDialog]);

  useEffect(() => {
    if (showDialog && step === wizardSteps.review && leafTopics.length === 0) {
      onClose();
    }
  }, [showDialog, step, leafTopics.length, onClose]);

  useEffect(() => {
    if (!showDialog) {
      setStep(wizardSteps.review);
      setPlacementByFolderKey({});
      setTopicsAddedCount(0);
      setAddingProgress({ added: 0, total: 0, batchIndex: 0, totalBatches: 0 });
      jobsRef.current = [];
      jobIndexRef.current = 0;
      addedLeafTopicsRef.current = [];
      onWizardBatchingChange?.(false);
      return;
    }
    setStep(wizardSteps.review);
    setExpandedSubjectIds(initialExpandedSubjectIdsForReview(subjectCards));
  }, [showDialog]);

  useEffect(() => {
    if (!showDialog || step !== wizardSteps.review) return;
    setExpandedSubjectIds(initialExpandedSubjectIdsForReview(subjectCards));
  }, [showDialog, step, subjectCards]);

  const sectionNeedsPlacement = useCallback(
    (card: SubjectReviewCard, _section: ReviewYearSection) => {
      if (card.isOffendingEducationSystem) return false;
      return needsPlacementOverride(card.educationSystemKey, studentEducationSystemKey);
    },
    [studentEducationSystemKey]
  );

  const allPlacementsValid = useMemo(() => {
    for (const card of subjectCards) {
      if (card.isOffendingEducationSystem) continue;
      for (const section of card.yearSections) {
        if (!sectionNeedsPlacement(card, section)) continue;
        const p = placementByFolderKey[section.folderKey];
        if (
          !p?.keyStageId?.trim() ||
          !p?.yearId?.trim() ||
          !isPlacementIdsValidForDefinition(studentDef, p.keyStageId, p.yearId)
        ) {
          return false;
        }
      }
    }
    return true;
  }, [subjectCards, placementByFolderKey, sectionNeedsPlacement, studentDef]);

  const canContinue =
    leafTopics.length > 0 &&
    esValidation.isValid &&
    allPlacementsValid &&
    subjectCards.every((c) => !c.isOffendingEducationSystem);

  const notifyCurriculumTreeAfterAdd = useCallback(() => {
    onPrepareCurriculumTreeView?.(
      collectEffectivePlacementsForAddedTopics(
        addedLeafTopicsRef.current,
        extractTopicMetadata,
        placementByFolderKeyRef.current,
        studentEducationSystemKey,
        defaultBuiltInProviderKey
      )
    );
  }, [
    onPrepareCurriculumTreeView,
    extractTopicMetadata,
    studentEducationSystemKey,
    defaultBuiltInProviderKey
  ]);

  const startAdding = () => {
    if (!canContinue || !studentId) return;
    const jobs = buildAddTopicsJobs(
      leafTopics,
      extractTopicMetadata,
      placementByFolderKey,
      studentEducationSystemKey,
      defaultBuiltInProviderKey
    );
    jobsRef.current = jobs;
    jobIndexRef.current = 0;
    addedLeafTopicsRef.current = [...leafTopics];
    setAddingProgress({
      added: 0,
      total: leafTopics.length,
      batchIndex: 0,
      totalBatches: jobs.length
    });
    onWizardBatchingChange?.(true);
    setStep(wizardSteps.adding);
    onRunJob(jobs[0]);
  };

  useEvent({
    eventName: 'E181v1_TopicsAddedToStudentCurriculum',
    onEventReceived: () => {
      const currentStep = stepRef.current;
      if (currentStep !== wizardSteps.adding && currentStep !== wizardSteps.cancelling) {
        return;
      }

      const job = jobsRef.current[jobIndexRef.current];
      const addedDelta = job?.topicIds.length ?? 0;

      setAddingProgress((prev) => {
        const added = prev.added + addedDelta;
        if (currentStep === wizardSteps.cancelling) {
          onWizardBatchingChange?.(false);
          setTopicsAddedCount(added);
          setStep(wizardSteps.complete);
          notifyCurriculumTreeAfterAdd();
          onReloadStudentCurriculum?.();
          return prev;
        }

        const nextIndex = jobIndexRef.current + 1;
        jobIndexRef.current = nextIndex;
        const jobs = jobsRef.current;
        if (nextIndex >= jobs.length) {
          onWizardBatchingChange?.(false);
          setTopicsAddedCount(added);
          setStep(wizardSteps.complete);
          notifyCurriculumTreeAfterAdd();
          onReloadStudentCurriculum?.();
          return { ...prev, added, batchIndex: nextIndex };
        }
        onRunJob(jobs[nextIndex]);
        return { ...prev, added, batchIndex: nextIndex };
      });
    }
  });

  useApiErrors(() => {
    if (stepRef.current === wizardSteps.adding || stepRef.current === wizardSteps.cancelling) {
      onWizardBatchingChange?.(false);
      setStep(wizardSteps.review);
    }
  });

  const toggleSubjectExpanded = (subjectId: string) => {
    setExpandedSubjectIds((prev) => {
      const next = new Set(prev);
      if (next.has(subjectId)) next.delete(subjectId);
      else next.add(subjectId);
      return next;
    });
  };

  const setPlacementForFolder = (folderKey: string, keyStageId: string, yearId: string) => {
    setPlacementByFolderKey((prev) => ({
      ...prev,
      [folderKey]: { keyStageId, yearId }
    }));
  };

  const ensurePlacementInitialized = (folderKey: string) => {
    if (placementByFolderKey[folderKey]) return;
    const firstKs = keyStagesWithIds[0];
    const firstYearId = firstKs?.years?.[0]?.yearId ?? '';
    if (firstKs?.keyStageId && firstYearId) {
      setPlacementForFolder(folderKey, firstKs.keyStageId, firstYearId);
    }
  };

  if (!showDialog) return null;

  const overlay = 'fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4';
  const panel =
    'bg-white rounded-lg shadow-xl max-w-2xl w-full mx-auto max-h-[85vh] flex flex-col overflow-hidden';
  const panelWide =
    'bg-white rounded-lg shadow-xl max-w-3xl w-full mx-auto max-h-[90vh] flex flex-col overflow-hidden';

  if (step === wizardSteps.adding || step === wizardSteps.cancelling) {
    const isCancelling = step === wizardSteps.cancelling;
    const { added, total, batchIndex, totalBatches } = addingProgress;
    const labels = describeAddTopicsBatchProgress(
      batchIndex,
      totalBatches || jobsRef.current.length,
      added,
      total
    );

    return (
      <Dialog open onOpenChange={() => {}}>
        <DialogContent dismissible={false} className="max-w-2xl sm:max-w-2xl">
              <h2 className="text-xl font-semibold text-gray-900 mb-4">
                {isCancelling ? 'Cancelling…' : 'Adding topics'}
              </h2>
              {labels.showBar && (
                <div className="mb-4">
                  <div>
                    <div className="flex justify-between text-sm text-gray-600 mb-1 gap-3">
                      <span className="font-medium text-gray-800">{labels.headline}</span>
                      <span className="shrink-0">{labels.percent}%</span>
                    </div>
                    <div className="w-full bg-gray-200 rounded-full h-2">
                      <div
                        className="bg-purple-600 h-2 rounded-full transition-all duration-300"
                        style={{ width: `${labels.percent}%` }}
                      />
                    </div>
                  </div>
                </div>
              )}
              {isCancelling ? (
                <ThinkingAnimation message="Finishing the current batch, then stopping…" />
              ) : (
                <ThinkingAnimation
                  progressLabel={{
                    current: added,
                    total,
                    suffix: 'topics added so far'
                  }}
                />
              )}
              {!isCancelling && (
                <div className="mt-4 flex justify-end">
                  <button
                    type="button"
                    onClick={() => setStep(wizardSteps.cancelling)}
                    className="px-4 py-2 text-sm font-medium text-gray-700 bg-gray-100 rounded-lg hover:bg-gray-200"
                  >
                    Cancel
                  </button>
                </div>
              )}
        </DialogContent>
      </Dialog>
    );
  }

  if (step === wizardSteps.complete) {
    const name = selectedStudentFirstName.trim() || 'Student';
    return (
      <div className={overlay}>
        <div className={panel}>
          <div className="p-6">
            <h2 className="text-xl font-semibold text-gray-900 mb-2">Topics added</h2>
            <p className="text-sm text-gray-600 mb-6">
              {topicsAddedCount} topic{topicsAddedCount === 1 ? '' : 's'} added to {name}&apos;s
              curriculum.
            </p>
            <div className="flex flex-col sm:flex-row gap-3 justify-end">
              <button
                type="button"
                onClick={() => {
                  onClearSelection();
                  onClose();
                }}
                className="px-4 py-2 text-sm font-medium text-gray-700 bg-white border border-gray-300 rounded-lg hover:bg-gray-50"
              >
                Add More Topics
              </button>
              <button
                type="button"
                onClick={() => {
                  onClearSelection();
                  onClose();
                  onViewStudentCurriculum(
                    collectEffectivePlacementsForAddedTopics(
                      addedLeafTopicsRef.current,
                      extractTopicMetadata,
                      placementByFolderKey,
                      studentEducationSystemKey,
                      defaultBuiltInProviderKey
                    )
                  );
                }}
                className="px-4 py-2 text-sm font-medium text-white bg-purple-600 rounded-lg hover:bg-purple-700"
              >
                View Topics in {name}&apos;s Curriculum
              </button>
            </div>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className={overlay}>
      <div className={panelWide}>
        <header className="p-6 border-b border-gray-200 flex items-center justify-between shrink-0">
          <div>
            <h2 className="text-xl font-semibold text-gray-900">Review topics to add</h2>
            <p className="text-sm text-gray-500 mt-1">
              Step 1 of 2 — confirm your selection ({leafTopics.length} topic
              {leafTopics.length === 1 ? '' : 's'})
            </p>
          </div>
          <button
            type="button"
            onClick={onClose}
            className="text-gray-400 hover:text-gray-600"
            aria-label="Close"
          >
            <X size={20} />
          </button>
        </header>

        <div className="p-6 overflow-y-auto flex-1 space-y-4 min-h-0">
              {!esValidation.isValid && (
                <div className="rounded-lg border border-destructive-action/30 bg-destructive-action/10 p-4 text-sm text-destructive-action-dark">
                  <p className="font-medium mb-1">Mixed education systems</p>
                  <p className="whitespace-pre-line">
                    All topics must use the same education system.{'\n'}
                    Remove topics from other frameworks (highlighted below) to continue.
                  </p>
                </div>
              )}

              {usesMultipleProviders && esValidation.isValid && (
                <p className="text-sm text-gray-600 border border-gray-200 rounded-lg p-3 bg-gray-50">
                  This selection includes both custom catalog and national curriculum topics. They
                  will be added in separate batches.
                </p>
              )}

              {subjectCards.map((card) => (
                <SubjectReviewCardView
                  key={card.subjectId}
                  card={card}
                  expanded={expandedSubjectIds.has(card.subjectId)}
                  onToggle={() => toggleSubjectExpanded(card.subjectId)}
                  placementDefs={placementDefs}
                  onRemoveTopic={onRemoveTopicById}
                  onRemoveAll={() => {
                    for (const section of card.yearSections) {
                      for (const row of section.topics) {
                        if (row.topicId) onRemoveTopicById(row.topicId);
                      }
                    }
                  }}
                  sectionNeedsPlacement={(section) => sectionNeedsPlacement(card, section)}
                  placementByFolderKey={placementByFolderKey}
                  studentDef={studentDef}
                  keyStagesWithIds={keyStagesWithIds}
                  ensurePlacementInitialized={ensurePlacementInitialized}
                  setPlacementForFolder={setPlacementForFolder}
                />
              ))}
        </div>

        <footer className="p-6 border-t border-gray-200 flex justify-end gap-3 shrink-0">
          <button
            type="button"
            onClick={onClose}
            className="px-4 py-2 text-sm font-medium text-gray-700 bg-white border border-gray-300 rounded-lg hover:bg-gray-50"
          >
            Cancel
          </button>
          <button
            type="button"
            onClick={startAdding}
            disabled={!canContinue}
            className="px-4 py-2 text-sm font-medium text-white bg-purple-600 rounded-lg hover:bg-purple-700 disabled:bg-gray-300 disabled:cursor-not-allowed"
          >
            Continue
          </button>
        </footer>
      </div>
    </div>
  );
}

function SubjectReviewCardView({
  card,
  expanded,
  onToggle,
  placementDefs,
  onRemoveTopic,
  onRemoveAll,
  sectionNeedsPlacement,
  placementByFolderKey,
  studentDef,
  keyStagesWithIds,
  ensurePlacementInitialized,
  setPlacementForFolder
}: {
  card: SubjectReviewCard;
  expanded: boolean;
  onToggle: () => void;
  placementDefs: EducationSystemDefinition[];
  onRemoveTopic: (id: string) => void;
  onRemoveAll: () => void;
  sectionNeedsPlacement: (section: ReviewYearSection) => boolean;
  placementByFolderKey: Record<string, PlacementOverride>;
  studentDef: EducationSystemDefinition | undefined;
  keyStagesWithIds: EducationSystemKeyStage[];
  ensurePlacementInitialized: (folderKey: string) => void;
  setPlacementForFolder: (folderKey: string, keyStageId: string, yearId: string) => void;
}) {
  const topicCount = card.yearSections.reduce((n, s) => n + s.topics.length, 0);
  const borderClass = card.isOffendingEducationSystem
    ? 'border-amber-400 bg-amber-50/50'
    : 'border-gray-200 bg-white';

  return (
    <div className={`rounded-lg border ${borderClass} overflow-hidden`}>
      <div className="flex items-center gap-2 px-4 py-3 border-b border-gray-100">
        <button
          type="button"
          onClick={onToggle}
          className="flex items-center gap-2 flex-1 min-w-0 text-left"
        >
          {expanded ? (
            <ChevronDown size={18} className="text-gray-500 shrink-0" />
          ) : (
            <ChevronRight size={18} className="text-gray-500 shrink-0" />
          )}
          <span className="font-medium text-gray-900 truncate">{card.subjectName}</span>
          <span className="text-xs text-gray-500 shrink-0">
            ({topicCount} topic{topicCount === 1 ? '' : 's'})
          </span>
        </button>
        {card.isOffendingEducationSystem && (
          <span className="text-xs font-medium text-amber-800 bg-amber-100 px-2 py-0.5 rounded">
            Different framework
          </span>
        )}
        <span className="text-xs text-gray-500 hidden sm:inline truncate max-w-[140px]">
          {educationSystemDisplayName(placementDefs, card.educationSystemKey)}
        </span>
        <button
          type="button"
          onClick={onRemoveAll}
          className="text-xs text-destructive-action hover:text-destructive-action-dark shrink-0"
        >
          Remove all
        </button>
      </div>

      {expanded && (
        <div className="px-4 py-3 space-y-4">
          {card.yearSections.map((section) => {
            if (sectionNeedsPlacement(section)) {
              ensurePlacementInitialized(section.folderKey);
            }
            const placement = placementByFolderKey[section.folderKey];
            const yearOptions = getYearsForKeyStageId(studentDef, placement?.keyStageId ?? '');

            return (
              <div key={section.folderKey}>
                <div className="space-y-2">
                  <p className="text-xs font-medium text-gray-600 uppercase tracking-wide">
                    {[section.keyStageName, section.yearLabel].filter(Boolean).join(' · ') ||
                      'Placement'}
                  </p>

                  {sectionNeedsPlacement(section) && keyStagesWithIds.length > 0 && (
                    <div className="border border-amber-200 bg-amber-50 rounded-lg p-3 space-y-2">
                      <p className="text-xs text-amber-900">
                        Place these topics in the student&apos;s curriculum (key stage and year).
                      </p>
                      <div className="grid grid-cols-1 sm:grid-cols-2 gap-2">
                        <div>
                          <label className="block text-xs font-medium text-gray-700 mb-1">
                            Key stage (student)
                          </label>
                          <select
                            className="w-full border border-gray-300 rounded-md px-2 py-1.5 text-sm"
                            value={placement?.keyStageId ?? ''}
                            onChange={(e) => {
                              const id = e.target.value;
                              const years = getYearsForKeyStageId(studentDef, id);
                              setPlacementForFolder(
                                section.folderKey,
                                id,
                                years[0]?.yearId ?? ''
                              );
                            }}
                          >
                            <option value="">Select</option>
                            {keyStagesWithIds.map((ks) => (
                              <option key={ks.keyStageId!} value={ks.keyStageId!}>
                                {ks.name}
                              </option>
                            ))}
                          </select>
                        </div>
                        <div>
                          <label className="block text-xs font-medium text-gray-700 mb-1">
                            Year (student)
                          </label>
                          <select
                            className="w-full border border-gray-300 rounded-md px-2 py-1.5 text-sm"
                            value={placement?.yearId ?? ''}
                            onChange={(e) =>
                              setPlacementForFolder(
                                section.folderKey,
                                placement?.keyStageId ?? '',
                                e.target.value
                              )
                            }
                            disabled={!placement?.keyStageId}
                          >
                            <option value="">Select</option>
                            {yearOptions.map((y) => (
                              <option key={y.yearId} value={y.yearId}>
                                {y.yearName}
                              </option>
                            ))}
                          </select>
                        </div>
                      </div>
                    </div>
                  )}

                  <ul className="space-y-1">
                    {section.topics.map((row) => (
                      <li
                        key={row.topicId}
                        className="flex items-start justify-between gap-2 py-1.5 px-2 rounded hover:bg-gray-50"
                      >
                        <div className="min-w-0">
                          <div className="text-sm font-medium text-gray-900">{row.name}</div>
                          {row.breadcrumb && (
                            <div className="text-xs text-gray-500 truncate">{row.breadcrumb}</div>
                          )}
                        </div>
                        <button
                          type="button"
                          onClick={() => onRemoveTopic(row.topicId)}
                          className="text-gray-400 hover:text-destructive-action shrink-0 p-1"
                          aria-label={`Remove ${row.name}`}
                        >
                          <Trash2 size={16} />
                        </button>
                      </li>
                    ))}
                  </ul>
                </div>
              </div>
            );
          })}
        </div>
      )}
    </div>
  );
}
